import { useEffect, useState } from 'preact/hooks';
import { api } from '../api.js';
import { goBack, showToast, friendlyError } from '../store.js';
import { __ } from '../i18n.js';
import Icon from '../components/Icon.jsx';
import Modal from '../components/Modal.jsx';
import ItemAddInput from '../components/ItemAddInput.jsx';
import {
  getListStore,
  refreshListStore,
  startShoppingPoll,
  checkItem,
  addItem,
  clearCheckedItems,
  isDirty,
} from '../lib/shoplist-sync.js';
import {
  SORT_DEFAULT,
  SORT_ALPHA,
  SORT_CATEGORY,
  sanitizeSortMode,
  sortItemsAlpha,
  groupItemsByCategory,
} from '../lib/shoplist-sort.js';
import { SHOPLIST_CATEGORY_VALUES, shoplistCategoryInfo } from '../lib/shoplist-categories.js';

/**
 * HH-5: winkelmodus (plan §4.2) — full-screen (no nav chrome: 'shopping'
 * is deliberately absent from NAV_VISIBLE/FAB_VISIBLE), oversized tap
 * targets, checked items sink into a collapsed "In de kar" tray,
 * tap-to-uncheck for mistakes, and the "klaar — lijst leegmaken?"
 * affordance once everything is checked.
 *
 * Every checkoff/add goes through the shared sync store: applied
 * locally the instant of the tap, synced in the background, kept queued
 * with the "Niet gesynchroniseerd" chip when the connection drops. The
 * ~10s freshness poll runs only while this screen is mounted (started
 * on entry, stopped in the unmount cleanup — the brief's "stopped on
 * leave", smoke-asserted).
 *
 * Sorting is display-only and per list: the shopper's own order is never
 * written back, so "eigen volgorde" always returns to exactly the list as
 * arranged. The chosen mode is remembered per list id (memory first,
 * localStorage best-effort — install.js's idiom), because a household
 * that walks one supermarket in aisle order walks it that way every week.
 */

/**
 * Per-list sort preference. Deliberately module scope rather than a
 * signal: nothing outside this screen reads it, and screens remount on
 * every navigation, so the state initializer is the natural read point.
 *
 * The stored string is NEVER trusted — localStorage is user-writable and
 * outlives the list's own grouping flag — so every read goes through
 * sanitizeSortMode() at render time, not here.
 */
const SORT_KEY_PREFIX = 'etm.shopSort.';
const memorySortModes = new Map();

function readSortMode(listId) {
  const key = SORT_KEY_PREFIX + listId;
  try {
    const stored = window.localStorage.getItem(key);
    if (stored) return stored;
  } catch (e) {
    // Storage unavailable — fall through to the in-memory value.
  }
  return memorySortModes.get(key) || SORT_DEFAULT;
}

function writeSortMode(listId, mode) {
  const key = SORT_KEY_PREFIX + listId;
  memorySortModes.set(key, mode);
  try {
    window.localStorage.setItem(key, mode);
  } catch (e) {
    // Private mode / storage disabled: the in-memory value still holds
    // for this session, which is the whole guarantee we need.
  }
}

/**
 * The sort picker. "Op categorie" only exists on a grouped list — offering
 * a mode that would render one undifferentiated "Overig" heading would be
 * worse than not offering it.
 */
function SortModal({ mode, groupByCategory, onClose, onPick }) {
  const options = [
    { value: SORT_DEFAULT, icon: 'format_list_bulleted', label: __('Eigen volgorde', 'etm') },
    { value: SORT_ALPHA, icon: 'sort_by_alpha', label: __('Alfabetisch', 'etm') },
  ];
  if (groupByCategory) {
    options.push({ value: SORT_CATEGORY, icon: 'sell', label: __('Op categorie', 'etm') });
  }

  return (
    <Modal title={__('Sorteren', 'etm')} onClose={onClose}>
      <div class="etm-quick-add-picker">
        {options.map((opt) => {
          const active = opt.value === mode;
          return (
            <button
              key={opt.value}
              type="button"
              class={`etm-quick-add-picker__item${active ? ' active' : ''}`}
              aria-pressed={active}
              onClick={() => onPick(opt.value)}
            >
              <Icon name={opt.icon} size={18} />
              {opt.label}
              {active ? <Icon name="check" size={18} /> : null}
            </button>
          );
        })}
      </div>
    </Modal>
  );
}

function ShopItem({ item, checked, onTap }) {
  return (
    <button type="button" class={`etm-shop-item${checked ? ' etm-shop-item--checked' : ''}`} onClick={onTap}>
      <span class="etm-shop-item__check" aria-hidden="true">
        {checked ? <Icon name="check" size={20} /> : null}
      </span>
      <span class="etm-shop-item__body">
        <span class="etm-shop-item__label">{item.label}</span>
        {item.note ? <span class="etm-shop-item__note">{item.note}</span> : null}
      </span>
    </button>
  );
}

export default function ShoppingScreen({ params }) {
  const listId = params?.listId;
  const store = getListStore(listId);

  const [history, setHistory] = useState([]);
  const [clearing, setClearing] = useState(false);
  const [sortMode, setSortMode] = useState(() => readSortMode(listId));
  const [showSort, setShowSort] = useState(false);

  useEffect(() => {
    let cancelled = false;

    refreshListStore(store).then((ok) => {
      // Only a first load with nothing to show warrants a toast; once
      // anything is on screen, a failed refresh silently keeps the
      // last-known state (the established stale-but-visible pattern).
      if (!cancelled && !ok && !store.loaded.peek()) {
        showToast(__('Er ging iets mis. Probeer het opnieuw.', 'etm'), 'error');
      }
    });

    // Autocomplete history is progressive enhancement here: offline it
    // simply doesn't load and typing still works (adds queue).
    api.lists
      .itemHistory()
      .then((res) => {
        if (!cancelled) setHistory(res.labels || []);
      })
      .catch(() => {});

    const stopPoll = startShoppingPoll(store);
    return () => {
      cancelled = true;
      stopPoll();
    };
  }, [listId]);

  const state = store.state.value;
  const meta = store.meta.value;
  const items = state.items;
  const open = items.filter((i) => !i.checked_at);
  const inKar = items.filter((i) => i.checked_at);
  const showSyncWarning = store.syncError.value && isDirty(store);

  // One line resolves both the stored-'category'-on-an-ungrouped-list case
  // and the "meta has not loaded yet" case — meta is null on first paint,
  // so `!!(meta && ...)` is falsy and the grouped view can never flash.
  const groupByCategory = !!(meta && meta.group_by_category);
  const effectiveSort = sanitizeSortMode(sortMode, groupByCategory);
  const groups = effectiveSort === SORT_CATEGORY ? groupItemsByCategory(open, SHOPLIST_CATEGORY_VALUES) : null;

  function pickSort(mode) {
    setSortMode(mode);
    writeSortMode(listId, mode);
    setShowSort(false);
  }

  function rememberInHistory(label) {
    setHistory((prev) => {
      const key = label.toLowerCase();
      if (prev.some((h) => h.toLowerCase() === key)) return prev;
      return [label, ...prev];
    });
  }

  function handleAdd(label) {
    addItem(store, label);
    rememberInHistory(label);
  }

  async function handleClear() {
    if (clearing) return;
    if (!confirm(__('Lijst leegmaken? Afgevinkte items gaan van de lijst, maar blijven beschikbaar als suggestie.', 'etm'))) {
      return;
    }
    setClearing(true);
    try {
      await clearCheckedItems(store);
      showToast(__('Lijst leeggemaakt.', 'etm'), 'success');
    } catch (err) {
      showToast(friendlyError(err), 'error');
    } finally {
      setClearing(false);
    }
  }

  return (
    <div class="etm-shop">
      <div class="etm-shop-bar">
        <button type="button" class="etm-icon-btn" aria-label={__('Winkelmodus sluiten', 'etm')} onClick={goBack}>
          <Icon name="close" size={24} />
        </button>
        <div class="etm-shop-bar__title">{meta ? meta.name : __('Lijstje', 'etm')}</div>
        <button type="button" class="etm-icon-btn" aria-label={__('Sorteren', 'etm')} onClick={() => setShowSort(true)}>
          <Icon name="sort" size={22} />
        </button>
        {showSyncWarning ? (
          <span class="etm-shop-sync">
            <Icon name="cloud_off" size={16} /> {__('Niet gesynchroniseerd', 'etm')}
          </span>
        ) : null}
      </div>

      <ItemAddInput history={history} onAdd={handleAdd} />

      {store.loaded.value && items.length === 0 ? (
        <div class="etm-empty-state">{__('Nog geen items.', 'etm')}</div>
      ) : null}

      {open.length === 0 && inKar.length > 0 ? (
        <div class="etm-shop-done">
          <Icon name="check_circle" size={44} />
          <p class="etm-shop-done__title">{__('Alles afgevinkt!', 'etm')}</p>
          <button type="button" class="etm-btn etm-btn-primary" disabled={clearing} onClick={handleClear}>
            <Icon name="delete_sweep" size={20} /> {__('Lijst leegmaken', 'etm')}
          </button>
        </div>
      ) : null}

      {groups ? (
        // Grouped: one header per non-empty category, in walk order, with
        // the untagged rows collecting in a trailing "Overig" section.
        // ShopItem itself is untouched — grouping only changes what wraps
        // it, so a checkoff inside a group is the same queue op it always
        // was.
        groups.map((group) => {
          const info = shoplistCategoryInfo(group.category);
          return (
            <div key={group.category === null ? '__rest__' : group.category}>
              <h3 class="etm-shop-group-header">
                <Icon name={info.icon} size={18} /> {info.label()}
              </h3>
              <div class="etm-shop-items">
                {group.items.map((item) => (
                  <ShopItem key={item.id} item={item} checked={false} onTap={() => checkItem(store, item.id, true)} />
                ))}
              </div>
            </div>
          );
        })
      ) : (
        <div class="etm-shop-items">
          {(effectiveSort === SORT_ALPHA ? sortItemsAlpha(open) : open).map((item) => (
            <ShopItem key={item.id} item={item} checked={false} onTap={() => checkItem(store, item.id, true)} />
          ))}
        </div>
      )}

      {inKar.length > 0 ? (
        <details class="etm-shop-kar">
          <summary class="etm-shop-kar__summary">
            <Icon name="shopping_cart" size={20} />
            <span>
              {__('In de kar', 'etm')} ({inKar.length})
            </span>
            <Icon name="keyboard_arrow_down" size={20} />
          </summary>
          <div class="etm-shop-items">
            {inKar.map((item) => (
              <ShopItem key={item.id} item={item} checked onTap={() => checkItem(store, item.id, false)} />
            ))}
          </div>
        </details>
      ) : null}

      {showSort ? (
        <SortModal
          mode={effectiveSort}
          groupByCategory={groupByCategory}
          onClose={() => setShowSort(false)}
          onPick={pickSort}
        />
      ) : null}
    </div>
  );
}
